import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Loader2, Search, Plus, Upload, Download, ArrowRight, ChevronLeft,
  Phone, Mail, Calendar, Users, FileText, Sparkles, Tag, MessageCircle,
  ExternalLink, Bookmark, X
} from 'lucide-react';
import { toast } from 'sonner';

const STAGE_MAP = { S0: 'Prospect', S1: 'Qualificato', S2: 'Opportunità', S3: 'Cliente' };
const STAGE_COLORS = {
  Prospect: { bg: '#F3F4F6', text: '#6B7280' }, Qualificato: { bg: '#DBEAFE', text: '#1D4ED8' },
  'Opportunità': { bg: '#FEF3C7', text: '#D97706' }, Cliente: { bg: '#D1FAE5', text: '#059669' },
  S0: { bg: '#F3F4F6', text: '#6B7280' }, S1: { bg: '#DBEAFE', text: '#1D4ED8' },
  S2: { bg: '#FEF3C7', text: '#D97706' }, S3: { bg: '#D1FAE5', text: '#059669' },
};

const SMART_FILTERS = [
  { id: 'lead_caldi', label: 'Lead caldi senza follow-up', emoji: '\uD83D\uDD25' },
  { id: 'ultimo_evento', label: 'Partecipanti ultimo evento', emoji: '\uD83D\uDCC6' },
  { id: 'newsletter', label: 'Newsletter attivi', emoji: '\uD83D\uDCF0' },
  { id: 'con_deal', label: 'Con deal attivo', emoji: '\uD83D\uDCB0' },
  { id: 'inattivi', label: 'Inattivi 30+ giorni', emoji: '\uD83D\uDE34' },
];

function daysSince(dateStr) {
  if (!dateStr) return 'Mai';
  const d = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (d === 0) return 'oggi';
  if (d === 1) return '1 giorno fa';
  return `${d} giorni fa`;
}

function scoreColor(s) {
  if (s >= 15) return '#059669';
  if (s >= 8) return '#D97706';
  return '#9CA3AF';
}

/* ─── Contact Detail (6 tabs) ─── */
function ContactDetail({ contact, onBack }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [engagements, setEngagements] = useState([]);
  const [deals, setDeals] = useState([]);
  const [events, setEvents] = useState([]);
  const [funnelState, setFunnelState] = useState(null);
  const [notes, setNotes] = useState(contact?.notes || []);
  const [newNote, setNewNote] = useState('');
  const [briefLoading, setBriefLoading] = useState(false);

  const loadTabData = useCallback(async (t) => {
    if (!contact?.id) return;
    try {
      if (t === 'history' && engagements.length === 0) {
        const res = await api.get(`/crm/contacts/${contact.id}/engagements`).catch(() => ({ data: [] }));
        setEngagements(res.data || []);
      }
      if (t === 'deals' && deals.length === 0) {
        const res = await api.get(`/crm/contacts/${contact.id}/deals`).catch(() => ({ data: [] }));
        setDeals(res.data?.deals || res.data || []);
      }
      if (t === 'events' && events.length === 0) {
        const res = await api.get(`/crm/contacts/${contact.id}/events`).catch(() => ({ data: [] }));
        setEvents(res.data?.events || res.data || []);
      }
      if (t === 'sequences') {
        const res = await api.get(`/funnel/contacts/${contact.id}/state`).catch(() => ({ data: null }));
        setFunnelState(res.data);
      }
    } catch {}
  }, [contact?.id]);

  useEffect(() => { loadTabData(tab); }, [tab, loadTabData]);

  const generateBrief = async () => {
    setBriefLoading(true);
    try {
      const res = await api.post(`/crm/contacts/${contact.id}/generate-brief`);
      toast.success('Brief aggiornato');
      if (res.data?.brief) contact.brief = res.data.brief;
    } catch { toast.error('Errore generazione brief'); }
    finally { setBriefLoading(false); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.patch(`/crm/contacts/${contact.id}`, {
        notes: [...notes, { text: newNote, created_at: new Date().toISOString(), author: 'Utente' }],
      });
      setNotes(prev => [...prev, { text: newNote, created_at: new Date().toISOString(), author: 'Utente' }]);
      setNewNote('');
      toast.success('Nota aggiunta');
    } catch { toast.error('Errore'); }
  };

  const stageLabel = STAGE_MAP[contact.stage] || contact.stage;
  const sc = STAGE_COLORS[contact.stage] || STAGE_COLORS.S0;

  return (
    <div className="flex flex-col h-full" data-testid="contact-detail">
      {/* Breadcrumb */}
      <button onClick={onBack}
        className="flex items-center gap-1.5 px-5 py-3 text-[13px] font-medium transition-colors hover:underline"
        style={{ color: '#2563EB' }}
        data-testid="contact-back-btn">
        <ChevronLeft size={16} /> Contatti & CRM
      </button>

      {/* Profile Header */}
      <div className="px-5 pb-4 flex items-start justify-between" style={{ borderBottom: '1px solid #E5E7EB' }}>
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full text-lg font-bold text-white"
            style={{ backgroundColor: '#2563EB' }}>
            {(contact.full_name || '??').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h1 className="text-[20px] font-bold" style={{ color: '#1B2B4B' }}>{contact.full_name}</h1>
            <p className="text-[14px]" style={{ color: '#7C8CA2' }}>
              {contact.role_title}{contact.role_title && contact.company ? ' · ' : ''}{contact.company}
            </p>
            <p className="text-[13px] mt-0.5" style={{ color: '#9CA3AF' }}>
              {contact.email}{contact.phone ? ` · ${contact.phone}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 rounded text-[12px] font-medium" style={{ backgroundColor: sc.bg, color: sc.text }}>
            {stageLabel}
          </span>
          <span className="text-[20px] font-bold" style={{ color: scoreColor(contact.score || 0) }}>
            {contact.score || 0}
          </span>
          {contact.phone && (
            <a href={`tel:${contact.phone}`} className="p-2 rounded-md hover:bg-gray-100 transition-colors">
              <Phone size={16} style={{ color: '#7C8CA2' }} />
            </a>
          )}
          {contact.email && (
            <a href={`mailto:${contact.email}`} className="p-2 rounded-md hover:bg-gray-100 transition-colors">
              <Mail size={16} style={{ color: '#7C8CA2' }} />
            </a>
          )}
        </div>
      </div>

      {/* 6 Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="px-5 pt-2 justify-start bg-transparent border-b rounded-none gap-0" style={{ borderColor: '#E5E7EB' }}>
          {[
            { v: 'overview', l: 'Panoramica' }, { v: 'history', l: 'Cronologia' },
            { v: 'deals', l: 'Opportunità' }, { v: 'sequences', l: 'Sequenze' },
            { v: 'connections', l: 'Connessioni' }, { v: 'notes', l: 'Note' },
          ].map(t => (
            <TabsTrigger key={t.v} value={t.v}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2563EB] data-[state=active]:text-[#2563EB] text-[13px] px-4 py-2.5"
              style={{ color: tab === t.v ? '#2563EB' : '#7C8CA2' }}
              data-testid={`tab-${t.v}`}>
              {t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="flex-1 overflow-y-auto p-5" style={{ backgroundColor: '#FAFBFC' }}>
          {/* Tab 1: Panoramica */}
          <TabsContent value="overview" className="mt-0">
            <div className="space-y-5">
              <div className="rounded-lg p-4" style={{ backgroundColor: '#F3F4F6' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[12px] font-medium" style={{ color: '#7C8CA2' }}>Brief</span>
                  <button onClick={generateBrief} disabled={briefLoading}
                    className="text-[12px] font-medium flex items-center gap-1 hover:underline"
                    style={{ color: '#2563EB' }}
                    data-testid="update-brief-btn">
                    {briefLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    Aggiorna brief
                  </button>
                </div>
                <p className="text-[14px] leading-relaxed line-clamp-4" style={{ color: '#1B2B4B' }}>
                  {contact.brief || 'Nessun brief disponibile. Clicca "Aggiorna brief" per generarlo.'}
                </p>
              </div>
              {(contact.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {contact.tags.map(tag => (
                    <span key={tag} className="px-2.5 py-1 rounded-full text-[12px]"
                      style={{ backgroundColor: '#DBEAFE', color: '#1D4ED8' }}>{tag}</span>
                  ))}
                </div>
              )}
              <div>
                <p className="text-[12px] font-medium mb-2" style={{ color: '#7C8CA2' }}>Azioni suggerite</p>
                <div className="grid grid-cols-3 gap-3">
                  <button onClick={() => navigate('/sales')}
                    className="rounded-lg p-3 text-left cursor-pointer hover:bg-blue-50 transition-colors"
                    style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
                    data-testid="action-proposta">
                    <p className="text-[13px] font-medium" style={{ color: '#2563EB' }}>Invia proposta</p>
                    <p className="text-[10px] mt-0.5" style={{ color: '#7C8CA2' }}>Apri pipeline vendite</p>
                  </button>
                  <button onClick={() => navigate('/events')}
                    className="rounded-lg p-3 text-left cursor-pointer hover:bg-blue-50 transition-colors"
                    style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
                    data-testid="action-workshop">
                    <p className="text-[13px] font-medium" style={{ color: '#2563EB' }}>Invita al Workshop</p>
                    <p className="text-[10px] mt-0.5" style={{ color: '#7C8CA2' }}>Vai agli eventi</p>
                  </button>
                  <button onClick={() => navigate('/command')}
                    className="rounded-lg p-3 text-left cursor-pointer hover:bg-blue-50 transition-colors"
                    style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
                    data-testid="action-call">
                    <p className="text-[13px] font-medium" style={{ color: '#2563EB' }}>Programma call</p>
                    <p className="text-[10px] mt-0.5" style={{ color: '#7C8CA2' }}>Apri Centro di Comando</p>
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Tab 2: Cronologia */}
          <TabsContent value="history" className="mt-0">
            <div className="relative pl-6">
              <div className="absolute left-2.5 top-0 bottom-0 w-px" style={{ backgroundColor: '#E5E7EB' }} />
              {engagements.length === 0 ? (
                <p className="text-[13px] py-4" style={{ color: '#7C8CA2' }}>Nessuna interazione registrata.</p>
              ) : engagements.map((e, i) => (
                <div key={i} className="relative pb-4">
                  <div className="absolute left-[-18px] top-1 w-3 h-3 rounded-full" style={{ backgroundColor: '#2563EB', border: '2px solid #FFFFFF' }} />
                  <p className="text-[13px]" style={{ color: '#1B2B4B' }}>
                    <span className="mr-2">{e.type === 'call' ? '\u{1F4DE}' : e.type === 'email' ? '\u{2709}\u{FE0F}' : e.type === 'event' ? '\u{1F4C6}' : '\u{1F464}'}</span>
                    {e.description || e.summary || e.type}
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: '#7C8CA2' }}>
                    {e.created_at ? new Date(e.created_at).toLocaleDateString('it-CH', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                  </p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Tab 3: Opportunità */}
          <TabsContent value="deals" className="mt-0">
            {deals.length === 0 ? (
              <p className="text-[13px]" style={{ color: '#7C8CA2' }}>Nessun deal collegato.</p>
            ) : deals.map((d, i) => (
              <div key={i} className="rounded-lg p-4 mb-3" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>{d.title || d.product || 'Deal'}</p>
                    <p className="text-[12px] mt-0.5" style={{ color: '#7C8CA2' }}>{d.stage || '-'} · {d.product || '-'}</p>
                  </div>
                  <span className="text-[16px] font-bold" style={{ color: '#059669' }}>
                    CHF {(d.value || 0).toLocaleString('it-CH')}
                  </span>
                </div>
                <button onClick={() => navigate('/sales')}
                  className="mt-2 text-[12px] font-medium hover:underline" style={{ color: '#2563EB' }}>
                  Vai alla Pipeline &rarr;
                </button>
              </div>
            ))}
          </TabsContent>

          {/* Tab 4: Sequenze */}
          <TabsContent value="sequences" className="mt-0">
            {funnelState ? (
              <div className="rounded-lg p-4" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                <p className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>
                  Sequenza: {funnelState.sequence_name || 'Attiva'}
                </p>
                <p className="text-[12px] mt-1" style={{ color: '#7C8CA2' }}>
                  Step {funnelState.current_step || 1}/{funnelState.total_steps || 5}
                </p>
                <div className="mt-2 h-1.5 rounded-full" style={{ backgroundColor: '#E5E7EB' }}>
                  <div className="h-1.5 rounded-full" style={{
                    backgroundColor: '#2563EB',
                    width: `${((funnelState.current_step || 1) / (funnelState.total_steps || 5)) * 100}%`,
                  }} />
                </div>
              </div>
            ) : (
              <p className="text-[13px]" style={{ color: '#7C8CA2' }}>Nessuna sequenza attiva per questo contatto.</p>
            )}
          </TabsContent>

          {/* Tab 5: Connessioni */}
          <TabsContent value="connections" className="mt-0">
            <p className="text-[13px]" style={{ color: '#7C8CA2' }}>Funzionalità in arrivo.</p>
          </TabsContent>

          {/* Tab 6: Note */}
          <TabsContent value="notes" className="mt-0">
            <div className="space-y-3 mb-4">
              {notes.length === 0 && (
                <p className="text-[13px]" style={{ color: '#7C8CA2' }}>Nessuna nota.</p>
              )}
              {notes.map((n, i) => (
                <div key={i} className="rounded-lg p-3" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                  <p className="text-[13px]" style={{ color: '#1B2B4B' }}>{n.text}</p>
                  <p className="text-[11px] mt-1" style={{ color: '#7C8CA2' }}>
                    {n.author || 'Utente'} · {n.created_at ? new Date(n.created_at).toLocaleDateString('it-CH') : ''}
                  </p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Aggiungi nota..."
                className="flex-1 rounded-lg px-4 py-2.5 text-[13px] outline-none focus:ring-2 focus:ring-[#2563EB]/20"
                style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }}
                data-testid="note-input" />
              <button onClick={addNote}
                className="px-4 py-2.5 rounded-lg text-[13px] font-medium text-white transition-colors hover:opacity-90"
                style={{ backgroundColor: '#2563EB' }} data-testid="add-note-btn">
                Aggiungi
              </button>
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}


/* ─── Main Page ─── */
export default function ContactsPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('all');
  const [smartFilter, setSmartFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newCompany, setNewCompany] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const loadContacts = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const res = await api.get('/crm/contacts', { params: { limit: 200, ...params } });
      const rawItems = res.data?.contacts || (Array.isArray(res.data) ? res.data : []);
      const items = rawItems.map(c => ({
        ...c,
        displayStage: STAGE_MAP[c.stage] || c.stage,
        initials: (c.full_name || '??').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase(),
      }));
      setContacts(items);
    } catch { toast.error('Errore caricamento contatti'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  const handleSmartFilter = async (filterId) => {
    if (smartFilter === filterId) { setSmartFilter(null); loadContacts(); return; }
    setSmartFilter(filterId);
    setStageFilter('all');
    try {
      switch (filterId) {
        case 'lead_caldi':
          await loadContacts({ score_min: 10, last_contact_days_max: 7 });
          break;
        case 'ultimo_evento': {
          const evRes = await api.get('/events?limit=1&sort=-event_date');
          const eventId = evRes.data?.[0]?.id;
          if (eventId) {
            const regs = await api.get(`/events/${eventId}/registrations`);
            const ids = (regs.data || []).map(r => r.contact_id).filter(Boolean);
            if (ids.length) setContacts(prev => prev.filter(c => ids.includes(c.id)));
          }
          break;
        }
        case 'newsletter':
          await loadContacts({ tag: 'newsletter' });
          break;
        case 'con_deal':
          await loadContacts({ has_deal: true });
          break;
        case 'inattivi':
          await loadContacts({ last_contact_days_min: 30 });
          break;
        default:
          break;
      }
    } catch { toast.error('Errore filtro'); }
  };

  const filteredContacts = contacts.filter(c => {
    if (stageFilter !== 'all' && c.stage !== stageFilter && c.displayStage !== stageFilter) return false;
    if (searchQuery && !(c.full_name || '').toLowerCase().includes(searchQuery.toLowerCase())
      && !(c.email || '').toLowerCase().includes(searchQuery.toLowerCase())
      && !(c.company || '').toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const stageFilters = [
    { key: 'all', label: 'Tutti', count: contacts.length },
    { key: 'S0', label: 'Prospect', count: contacts.filter(c => c.stage === 'S0').length },
    { key: 'S1', label: 'Qualificato', count: contacts.filter(c => c.stage === 'S1').length },
    { key: 'S2', label: 'Opportunità', count: contacts.filter(c => c.stage === 'S2').length },
    { key: 'S3', label: 'Cliente', count: contacts.filter(c => c.stage === 'S3').length },
  ];

  const handleSelectAll = () => {
    setSelectedIds(prev => prev.length === filteredContacts.length ? [] : filteredContacts.map(c => c.id));
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleCreateContact = async () => {
    if (!newName.trim() || !newEmail.trim()) return;
    setActionLoading(true);
    try {
      await api.post('/crm/contacts', { full_name: newName, email: newEmail, company: newCompany });
      toast.success('Contatto creato');
      setShowNewDialog(false);
      setNewName(''); setNewEmail(''); setNewCompany('');
      loadContacts();
    } catch { toast.error('Errore creazione'); }
    finally { setActionLoading(false); }
  };

  const openDetail = async (contact) => {
    try {
      const res = await api.get(`/crm/contacts/${contact.id}`);
      setSelectedContact(res.data || contact);
    } catch {
      setSelectedContact(contact);
    }
  };

  if (loading && contacts.length === 0) {
    return (
      <div className="flex items-center justify-center h-full" style={{ backgroundColor: '#FAFBFC' }}
        data-testid="contacts-loading">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#2563EB' }} />
      </div>
    );
  }

  /* Detail view */
  if (selectedContact) {
    return (
      <div className="h-[calc(100vh-64px)]" style={{ backgroundColor: '#FAFBFC' }}>
        <ContactDetail contact={selectedContact} onBack={() => setSelectedContact(null)} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]" data-testid="contacts-page" style={{ backgroundColor: '#FAFBFC' }}>
      {/* Header */}
      <div className="px-5 py-4 flex-shrink-0" style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}>
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-[18px] font-bold" style={{ color: '#1B2B4B' }}>Contatti & CRM</h1>
          <div className="flex gap-2">
            <button onClick={() => setShowImportDialog(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors hover:bg-gray-50"
              style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }}
              data-testid="import-btn">
              <Upload size={14} />Importa
            </button>
            <button onClick={() => setShowNewDialog(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-medium text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: '#2563EB' }}
              data-testid="new-contact-btn">
              <Plus size={14} />Nuovo contatto
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#7C8CA2' }} />
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cerca per nome, email, azienda..."
            className="w-full pl-9 pr-3 py-2 rounded-lg text-[13px] outline-none focus:ring-2 focus:ring-[#2563EB]/20"
            style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }}
            data-testid="contact-search" />
        </div>

        {/* Stage filters */}
        <div className="flex gap-2 mb-2">
          {stageFilters.map(f => (
            <button key={f.key} onClick={() => { setStageFilter(f.key); setSmartFilter(null); loadContacts(); }}
              className="px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors"
              style={{
                backgroundColor: stageFilter === f.key ? '#DBEAFE' : '#F3F4F6',
                color: stageFilter === f.key ? '#2563EB' : '#6B7280',
                border: stageFilter === f.key ? '1px solid #2563EB' : '1px solid transparent',
              }}
              data-testid={`stage-filter-${f.key}`}>
              {f.label} ({f.count})
            </button>
          ))}
        </div>

        {/* Smart filters */}
        <div className="flex gap-2">
          {SMART_FILTERS.map(f => (
            <button key={f.id} onClick={() => handleSmartFilter(f.id)}
              className="px-3 py-1.5 rounded-full text-[12px] transition-colors"
              style={{
                backgroundColor: smartFilter === f.id ? '#DBEAFE' : '#F9FAFB',
                color: smartFilter === f.id ? '#2563EB' : '#7C8CA2',
                border: smartFilter === f.id ? '1px solid #2563EB' : '1px solid #E5E7EB',
              }}
              data-testid={`smart-filter-${f.id}`}>
              {f.emoji} {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Batch actions */}
      {selectedIds.length > 0 && (
        <div className="px-5 py-2 flex items-center justify-between flex-shrink-0"
          style={{ backgroundColor: '#EFF6FF', borderBottom: '1px solid #E5E7EB' }}
          data-testid="batch-bar">
          <span className="text-[13px] font-medium" style={{ color: '#1B2B4B' }}>{selectedIds.length} selezionati</span>
          <div className="flex gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors opacity-60 cursor-not-allowed"
                    style={{ backgroundColor: '#F3F4F6', color: '#9CA3AF' }}>
                    <Tag size={12} className="inline mr-1" />Tagga
                  </button>
                </TooltipTrigger>
                <TooltipContent><p className="text-xs">Funzionalità in sviluppo</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors opacity-60 cursor-not-allowed"
                    style={{ backgroundColor: '#F3F4F6', color: '#9CA3AF' }}>
                    <Calendar size={12} className="inline mr-1" />Invita
                  </button>
                </TooltipTrigger>
                <TooltipContent><p className="text-xs">Funzionalità in sviluppo</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <button onClick={() => setSelectedIds([])}
              className="px-3 py-1.5 rounded-md text-[12px] font-medium hover:bg-gray-50"
              style={{ border: '1px solid #E5E7EB', color: '#6B7280' }}>Annulla</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto px-5">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent" style={{ borderColor: '#E5E7EB' }}>
              <TableHead style={{ width: 40 }}>
                <Checkbox checked={selectedIds.length === filteredContacts.length && filteredContacts.length > 0}
                  onCheckedChange={handleSelectAll} data-testid="select-all" />
              </TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider" style={{ color: '#7C8CA2', width: '25%' }}>Contatto</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider" style={{ color: '#7C8CA2', width: '15%' }}>Email</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider" style={{ color: '#7C8CA2', width: '15%' }}>Tag</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider" style={{ color: '#7C8CA2', width: '12%' }}>Ultimo contatto</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider" style={{ color: '#7C8CA2', width: '10%' }}>Stage</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider" style={{ color: '#7C8CA2', width: '8%' }}>Score</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider" style={{ color: '#7C8CA2', width: '15%' }}>Azione</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredContacts.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12">
                <Users size={32} className="mx-auto mb-3" style={{ color: '#D1D5DB' }} />
                <p className="text-[14px]" style={{ color: '#7C8CA2' }}>Nessun contatto trovato</p>
              </TableCell></TableRow>
            ) : filteredContacts.map(c => {
              const sc = STAGE_COLORS[c.stage] || STAGE_COLORS.S0;
              return (
                <TableRow key={c.id} className="hover:bg-blue-50/30" style={{ borderColor: '#E5E7EB' }}
                  data-testid={`contact-row-${c.id}`}>
                  <TableCell>
                    <Checkbox checked={selectedIds.includes(c.id)}
                      onCheckedChange={() => handleSelectOne(c.id)} />
                  </TableCell>
                  <TableCell>
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <button onClick={() => openDetail(c)} className="flex items-center gap-3 text-left group"
                          data-testid={`contact-name-${c.id}`}>
                          <div className="flex h-9 w-9 items-center justify-center rounded-full text-[11px] font-bold text-white flex-shrink-0"
                            style={{ backgroundColor: '#2563EB' }}>
                            {c.initials}
                          </div>
                          <div>
                            <p className="text-[14px] font-semibold group-hover:text-[#2563EB] transition-colors" style={{ color: '#1B2B4B' }}>
                              {c.full_name}
                            </p>
                            <p className="text-[12px]" style={{ color: '#7C8CA2' }}>
                              {c.company}{c.role_title ? ` · ${c.role_title}` : ''}
                            </p>
                          </div>
                        </button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-72 p-4" side="right" data-testid={`hover-card-${c.id}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full text-[12px] font-bold text-white"
                            style={{ backgroundColor: '#2563EB' }}>{c.initials}</div>
                          <div>
                            <p className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>{c.full_name}</p>
                            <p className="text-[12px]" style={{ color: '#7C8CA2' }}>{c.company}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 rounded text-[11px] font-medium" style={{ backgroundColor: sc.bg, color: sc.text }}>
                            {c.displayStage}
                          </span>
                          <span className="text-[14px] font-bold" style={{ color: scoreColor(c.score || 0) }}>{c.score || 0}</span>
                        </div>
                        <p className="text-[12px] mb-2" style={{ color: '#7C8CA2' }}>
                          Ultimo contatto: {daysSince(c.last_interaction_at)}
                        </p>
                        <button onClick={() => openDetail(c)}
                          className="text-[12px] font-medium hover:underline" style={{ color: '#2563EB' }}>
                          Apri profilo completo &rarr;
                        </button>
                      </HoverCardContent>
                    </HoverCard>
                  </TableCell>
                  <TableCell className="text-[13px] truncate max-w-[150px]" style={{ color: '#4B5563' }}>
                    {c.email || '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(c.tags || []).slice(0, 2).map(tag => (
                        <span key={tag} className="px-2 py-0.5 rounded-full text-[10px]"
                          style={{ backgroundColor: '#DBEAFE', color: '#1D4ED8' }}>{tag}</span>
                      ))}
                      {(c.tags || []).length > 2 && (
                        <span className="px-1.5 py-0.5 rounded-full text-[10px]" style={{ backgroundColor: '#F3F4F6', color: '#7C8CA2' }}>
                          +{c.tags.length - 2}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-[12px]" style={{ color: '#7C8CA2' }}>
                    {daysSince(c.last_interaction_at)}
                  </TableCell>
                  <TableCell>
                    <span className="px-2 py-0.5 rounded text-[11px] font-medium"
                      style={{ backgroundColor: sc.bg, color: sc.text }}>
                      {c.displayStage}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-[16px] font-bold" style={{ color: scoreColor(c.score || 0) }}>
                      {c.score || 0}
                    </span>
                  </TableCell>
                  <TableCell>
                    <button onClick={() => openDetail(c)}
                      className="text-[13px] font-medium flex items-center gap-1 hover:underline"
                      style={{ color: '#2563EB' }}
                      data-testid={`action-${c.id}`}>
                      {c.next_action || 'Dettaglio'} <ArrowRight size={14} />
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Bottom bar */}
      <div className="px-5 py-3 flex items-center justify-between flex-shrink-0"
        style={{ backgroundColor: '#FFFFFF', borderTop: '1px solid #E5E7EB' }}>
        <span className="text-[12px]" style={{ color: '#7C8CA2' }}>{filteredContacts.length} contatti</span>
        <div className="flex gap-2">
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] transition-colors hover:bg-gray-50"
            style={{ border: '1px solid #E5E7EB', color: '#7C8CA2' }}
            onClick={() => {
              try {
                const contacts = selectedIds.length > 0
                  ? filteredContacts.filter(c => selectedIds.includes(c.id))
                  : filteredContacts;
                const headers = ['Nome', 'Email', 'Azienda', 'Ruolo', 'Score', 'Stage'];
                const rows = contacts.map(c => [
                  c.full_name || c.name || '', c.email || '', c.company || '', c.role || '', c.score || '', c.stage || ''
                ]);
                const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
                const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'contatti.csv'; a.click();
                URL.revokeObjectURL(url);
                toast.success(`Esportati ${contacts.length} contatti`);
              } catch { toast.error('Errore esportazione'); }
            }}>
            <Download size={14} />Esporta lista
          </button>
        </div>
      </div>

      {/* New Contact Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>Nuovo contatto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nome completo"
              className="w-full rounded-lg px-4 py-2.5 text-[13px] outline-none focus:ring-2 focus:ring-[#2563EB]/20"
              style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }} data-testid="new-name-input" />
            <input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Email"
              className="w-full rounded-lg px-4 py-2.5 text-[13px] outline-none focus:ring-2 focus:ring-[#2563EB]/20"
              style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }} data-testid="new-email-input" />
            <input value={newCompany} onChange={e => setNewCompany(e.target.value)} placeholder="Azienda"
              className="w-full rounded-lg px-4 py-2.5 text-[13px] outline-none focus:ring-2 focus:ring-[#2563EB]/20"
              style={{ border: '1px solid #E5E7EB', color: '#1B2B4B' }} />
            <button onClick={handleCreateContact} disabled={actionLoading || !newName.trim() || !newEmail.trim()}
              className="w-full py-2.5 rounded-lg text-[13px] font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#2563EB' }} data-testid="create-contact-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Crea contatto'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[14px] font-semibold" style={{ color: '#1B2B4B' }}>Importa contatti CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg p-8 text-center" style={{ border: '2px dashed #E5E7EB', backgroundColor: '#FAFBFC' }}>
              <Upload size={32} className="mx-auto mb-3" style={{ color: '#D1D5DB' }} />
              <p className="text-[13px]" style={{ color: '#7C8CA2' }}>Trascina qui il file CSV o clicca per selezionare</p>
              <input type="file" accept=".csv" className="mt-3" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('file', file);
                try {
                  await api.post('/crm/import-bulk', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                  toast.success('Importazione completata');
                  setShowImportDialog(false);
                  loadContacts();
                } catch { toast.error('Errore importazione'); }
              }} />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
